import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OrderHeader = ({ order, onStatusChange, onEdit, onInvoice }) => {
  const getStatusColor = (status) => {
    const colors = {
      NEW: 'bg-blue-100 text-blue-800',
      IN_REVIEW: 'bg-yellow-100 text-yellow-800',
      ASSIGNED: 'bg-purple-100 text-purple-800',
      IN_PROGRESS: 'bg-orange-100 text-orange-800',
      DONE: 'bg-green-100 text-green-800',
      INVOICED: 'bg-gray-100 text-gray-800',
      CANCELLED: 'bg-red-100 text-red-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      LOW: 'text-green-600',
      NORMAL: 'text-blue-600',
      HIGH: 'text-orange-600',
      URGENT: 'text-red-600'
    };
    return colors?.[priority] || 'text-gray-600';
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      NEW: 'IN_REVIEW',
      IN_REVIEW: 'ASSIGNED',
      ASSIGNED: 'IN_PROGRESS',
      IN_PROGRESS: 'DONE',
      DONE: 'INVOICED'
    };
    return statusFlow?.[currentStatus];
  };

  const getStatusLabel = (status) => {
    const labels = {
      NEW: 'Neu',
      IN_REVIEW: 'In Prüfung',
      ASSIGNED: 'Zugewiesen',
      IN_PROGRESS: 'In Bearbeitung',
      DONE: 'Abgeschlossen',
      INVOICED: 'Abgerechnet',
      CANCELLED: 'Storniert'
    };
    return labels?.[status] || status;
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      LOW: 'Niedrig',
      NORMAL: 'Normal',
      HIGH: 'Hoch',
      URGENT: 'Dringend'
    };
    return labels?.[priority] || priority;
  };

  const nextStatus = getNextStatus(order?.status);

  return (
    <div className="bg-card rounded-lg border border-border p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-text-primary">
              Auftrag #{order?.orderNumber}
            </h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order?.status)}`}>
              {getStatusLabel(order?.status)}
            </span>
          </div>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary">
            <div className="flex items-center gap-1">
              <Icon name="Calendar" size={16} />
              <span>Erstellt: {new Date(order.createdAt)?.toLocaleDateString('de-DE')}</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="Clock" size={16} />
              <span>Letzte Änderung: {new Date(order.updatedAt)?.toLocaleDateString('de-DE')}</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="AlertTriangle" size={16} />
              <span className={getPriorityColor(order?.priority)}>
                Priorität: {getPriorityLabel(order?.priority)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={onEdit}
            iconName="Edit"
            iconPosition="left"
          >
            Bearbeiten
          </Button>
          
          {nextStatus && (
            <Button
              variant="default"
              onClick={() => onStatusChange(nextStatus)}
              iconName="ArrowRight"
              iconPosition="right"
            >
              {getStatusLabel(nextStatus)}
            </Button>
          )}
          
          {order?.status === 'DONE' && (
            <Button
              variant="success"
              onClick={onInvoice}
              iconName="FileText"
              iconPosition="left"
            >
              Rechnung erstellen
            </Button>
          )}
          
          {order?.status !== 'INVOICED' && order?.status !== 'CANCELLED' && (
            <Button
              variant="destructive"
              onClick={() => onStatusChange('CANCELLED')}
              iconName="X"
              iconPosition="left"
            >
              Stornieren
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderHeader;